import React, { useEffect, useRef, useState } from "react"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Alert, Checkbox, Collapse, InputNumber, Modal, Select } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { ROUTES } from "constants/routes"
import { queryKeys } from "api/queryKeys"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { datafileText, generateDatamap } from "api/Repos/data-import"
import { downloadDataAsFile } from "utils/files/downloadUtils"
import { errMsgOf, failureOf } from "utils/errMsgOf"
import { EXPORT_VARIABLE_TYPES, VARIABLE_STYLE } from "types/engineExport.types"
import { isConflict, useDummyData, useMapIdVariables } from "../api/useDummyData"
import {
  DummyGenerateResult,
  DummyMapOptions,
  DummyProfile,
} from "types/dummyData.types"
import DummyRunReport from "./DummyRunReport.component"
import QuestionSettings from "./QuestionSettings.component"

/** Tab keys of ProjectDetailsTabs. */
const TABLES_TAB = PROJECT_TAB_KEYS.TABLES
const DASHBOARDS_TAB = PROJECT_TAB_KEYS.DASHBOARDS
const DEFAULT_RESPONDENTS = 500
const DEFAULT_MAP: DummyMapOptions = {
  var: "numbering",
  labelStyle: "default",
  questionText: false,
  groupSections: false,
}

interface Props {
  projectId: string
  owner: string
  projectName: string
  /**
   * Whether the project has a map; "unknown" until its files were checked,
   * "failed" when they could not be checked yet.
   */
  hasMap: boolean | "unknown" | "failed"
  /** Reads the project's files again, after they could not be checked. */
  onRetryFiles?: () => void
  onGenerated: () => void
}

const newSeed = (): number => Math.floor(Math.random() * 1_000_000_000)

/**
 * The map naming options, worded as the Generate Map dialog words them
 * (DataImportTable), since they make the same map.
 */
const MapOptionsFields: React.FC<{
  value: DummyMapOptions
  onChange: (value: DummyMapOptions) => void
  testId?: string
  disabled?: boolean
}> = ({ value, onChange, testId, disabled }) => (
  <div className="flex flex-wrap items-end gap-4" data-testid={testId}>
    <div className="flex flex-col text-sm">
      <span>Use as variable names</span>
      <Select
        aria-label="Use as variable names"
        className="min-w-56"
        disabled={disabled}
        value={value.var}
        onChange={(v) => onChange({ ...value, var: v })}
        options={[
          { value: "numbering", label: "Numbered automatically" },
          { value: "labels", label: "Generate from questionnaire labels" },
        ]}
      />
    </div>
    <div className="flex flex-col text-sm">
      <span>Variable style</span>
      <Select
        aria-label="Variable style"
        className="min-w-56"
        disabled={disabled}
        value={value.labelStyle}
        onChange={(v) => onChange({ ...value, labelStyle: v })}
        options={[
          { value: "default", label: "Default (s/p prefixes)" },
          { value: "decipher", label: "Decipher standard (r/c prefixes)" },
        ]}
      />
    </div>
    <Checkbox
      checked={value.groupSections}
      disabled={disabled}
      onChange={(e) => onChange({ ...value, groupSections: e.target.checked })}
    >
      Group questionnaire sections
    </Checkbox>
    <Checkbox
      checked={value.questionText}
      disabled={disabled}
      onChange={(e) => onChange({ ...value, questionText: e.target.checked })}
    >
      Include full question text
    </Checkbox>
  </div>
)

/**
 * The "Dummy data" import method: respondents, seed, off-path rate, the
 * respondent ID column (only when the project has a map), the map naming
 * (only when it has none yet) and per-question settings, then Generate.
 * Generate waits until the last run's settings (read from the DQD's
 * provenance) and the project's files are known.
 * A project with imported data asks before it is replaced. After a run it
 * shows the run report, with the downloads, Regenerate map and the links to
 * Tables and Dashboards, until the data stops being dummy data.
 */
const DummyDataPanel: React.FC<Props> = ({
  projectId,
  owner,
  projectName,
  hasMap,
  onRetryFiles,
  onGenerated,
}) => {
  const navigate = useNavigate()
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()
  const {
    state,
    isActive,
    generate,
    isGenerating,
    isLoading,
    isError,
    retry,
    refresh,
    readFresh,
  } = useDummyData(projectId)
  // Until the files were checked, which map options apply is not known.
  const mapKnown = typeof hasMap === "boolean"
  const ids = useMapIdVariables(projectId, hasMap === true)
  // The settings wait for the last run's (which would replace an edit made
  // meanwhile) and hold still while a run is generating.
  const holdSettings = isGenerating || isLoading
  const [profile, setProfile] = useState<DummyProfile>(() => ({
    respondents: DEFAULT_RESPONDENTS,
    seed: newSeed(),
    offPathRate: 0,
  }))
  const [mapOptions, setMapOptions] = useState<DummyMapOptions>(DEFAULT_MAP)
  const [result, setResult] = useState<DummyGenerateResult | null>(null)
  // The naming the Regenerate map dialog asks for; null while it is closed.
  const [askedMap, setAskedMap] = useState<DummyMapOptions | null>(null)
  const [isRegenerating, setIsRegenerating] = useState(false)
  // A new map was made since the run shown: its map findings are out of date.
  const [mapRegenerated, setMapRegenerated] = useState(false)
  // The naming of the map made here last. Saving a map makes the server
  // forget the last run's naming, so the dialog starts from this one.
  const lastNaming = useRef<DummyMapOptions | null>(null)

  // Once the data is no longer dummy data (removed, or real data imported),
  // the run shown no longer describes it: Download CSV would fetch other data.
  const wasActive = useRef(isActive)
  useEffect(() => {
    if (wasActive.current && !isActive) {
      setResult(null)
      setMapRegenerated(false)
    }
    wasActive.current = isActive
  }, [isActive])

  // The profile and the map naming of the run that made the data (the
  // DQD's provenance) replace the defaults once per project, and again only
  // after a new run (its time changes). A plain refetch of the same state
  // leaves the user's edits alone.
  const seededFrom = useRef<string | null>(null)
  useEffect(() => {
    if (!state) return
    const from = JSON.stringify([projectId, state.provenance?.generatedAt ?? ""])
    if (seededFrom.current === from) return
    seededFrom.current = from
    const saved = state.provenance?.profile
    setProfile((p) => ({
      respondents: DEFAULT_RESPONDENTS,
      offPathRate: 0,
      ...saved,
      seed: saved?.seed ?? p.seed,
    }))
    setMapOptions(state.provenance?.mapOptions ?? DEFAULT_MAP)
  }, [state, projectId])

  const run = async (replace: boolean): Promise<void> => {
    try {
      const res = await generate({
        profile,
        mapOptions: hasMap === true ? undefined : mapOptions,
        replace,
      })
      setResult(res)
      setMapRegenerated(false)
      notify.toast.success("Dummy data generated")
      onGenerated()
    } catch (e) {
      if (isConflict(e)) {
        Modal.confirm({
          title: "Replace your imported data?",
          content:
            "This project already has imported data. Generating dummy data replaces it.",
          okText: "Replace",
          onOk: () => run(true),
        })
        return
      }
      notify.error("Could not generate dummy data", failureOf(e))
    }
  }

  /**
   * Makes a new map with the given naming, replacing the project's map.
   * Returns whether it worked.
   */
  const regenerateMap = async (o: DummyMapOptions): Promise<boolean> => {
    setIsRegenerating(true)
    try {
      const res = await generateDatamap(
        await getAxios(),
        projectId,
        o.groupSections,
        o.var === "labels"
          ? EXPORT_VARIABLE_TYPES.LABELS
          : EXPORT_VARIABLE_TYPES.NUMBERING,
        o.labelStyle === "decipher"
          ? VARIABLE_STYLE.DECIPHER
          : VARIABLE_STYLE.DEFAULT,
        o.questionText,
        true
      )
      const msg = errMsgOf(res.data)
      if (msg !== null) throw new Error(msg)
      lastNaming.current = o
      setMapRegenerated(true)
      // The map's columns changed.
      refresh()
      void queryClient.invalidateQueries({
        queryKey: queryKeys.datafileExists.project(projectId),
      })
      return true
    } catch (e) {
      // The server's message, from a 200 answer's body or an error status's.
      notify.error("Could not regenerate the map", failureOf(e))
      return false
    } finally {
      setIsRegenerating(false)
    }
  }

  // The new map replaces the project's map, so the naming is always asked
  // for, starting from the naming the map was made with when it is known.
  const onRegenerateMap = (): void =>
    setAskedMap(lastNaming.current ?? state?.provenance?.mapOptions ?? DEFAULT_MAP)

  // "None" first; a saved column the map no longer has stays shown. With
  // nothing to pick and nothing picked (no INT or TEXT column, or a map whose
  // header the list cannot read) there is no choice to offer.
  const idNames = ids.variables ?? []
  const showIds =
    hasMap === true && (idNames.length > 0 || !!profile.idVariable)
  const idOptions = [
    { value: "", label: "None" },
    ...[
      ...idNames,
      ...(profile.idVariable && !idNames.includes(profile.idVariable)
        ? [profile.idVariable]
        : []),
    ].map((v) => ({ value: v, label: v })),
  ]

  const download = async (subtype: "csv" | "map"): Promise<void> => {
    try {
      const text = await datafileText(await getAxios(), projectId, subtype)
      downloadDataAsFile(
        text,
        `${projectName || "project"}_${subtype === "csv" ? "dummy_data" : "map"}.csv`,
        "text/csv;charset=utf-8"
      )
    } catch (e) {
      notify.error(
        subtype === "csv"
          ? "Could not download the data"
          : "Could not download the map",
        failureOf(e)
      )
    }
  }

  // Only the run's own CSV is downloaded as dummy data: the server is asked
  // again, as a real CSV may have replaced it since the run.
  const downloadCsv = async (): Promise<void> => {
    try {
      const now = await readFresh({ csv: true })
      if (!now.csvIsDummy) {
        notify.error(
          "Could not download the data",
          "The project's CSV is no longer this run's dummy data."
        )
        return
      }
    } catch (e) {
      notify.error("Could not download the data", failureOf(e))
      return
    }
    await download("csv")
  }

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="dummy-data-panel">
      <p className="m-0 text-sm">
        Generate respondents that follow the questionnaire&apos;s routing. They
        are loaded like an import, and the data is marked as dummy data until
        you remove it.
      </p>
      {hasMap === "failed" && (
        <Alert
          type="error"
          showIcon
          message="Could not check the project's files."
          action={
            onRetryFiles && (
              <DataQButton size="small" onClick={onRetryFiles}>
                Retry
              </DataQButton>
            )
          }
        />
      )}
      {isError && (
        <Alert
          type="error"
          showIcon
          message="Could not read this project's dummy data state."
          action={
            <DataQButton size="small" onClick={retry}>
              Retry
            </DataQButton>
          }
        />
      )}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col text-sm">
          <span>Respondents</span>
          <InputNumber
            aria-label="Respondents"
            min={1}
            max={10000}
            precision={0}
            disabled={holdSettings}
            value={profile.respondents}
            onChange={(v) =>
              setProfile((p) => ({ ...p, respondents: v ?? DEFAULT_RESPONDENTS }))
            }
          />
        </div>
        <div className="flex flex-col text-sm">
          <span>Seed</span>
          <div className="flex gap-2">
            <InputNumber
              aria-label="Seed"
              min={0}
              precision={0}
              disabled={holdSettings}
              value={profile.seed}
              onChange={(v) => setProfile((p) => ({ ...p, seed: v ?? 0 }))}
            />
            <DataQButton
              isDisabled={holdSettings}
              onClick={() => setProfile((p) => ({ ...p, seed: newSeed() }))}
            >
              New seed
            </DataQButton>
          </div>
        </div>
        <div className="flex flex-col text-sm">
          <span>Off-path answers (%)</span>
          <InputNumber
            aria-label="Off-path answers"
            min={0}
            max={100}
            disabled={holdSettings}
            value={profile.offPathRate ?? 0}
            onChange={(v) => setProfile((p) => ({ ...p, offPathRate: v ?? 0 }))}
          />
        </div>
        {showIds && (
          <div className="flex flex-col text-sm">
            <span>Respondent ID column</span>
            <Select
              aria-label="Respondent ID column"
              className="min-w-56"
              disabled={holdSettings}
              loading={ids.isLoading}
              value={profile.idVariable ?? ""}
              onChange={(v: string) =>
                setProfile((p) => {
                  const next = { ...p }
                  if (v) next.idVariable = v
                  else delete next.idVariable
                  return next
                })
              }
              options={idOptions}
            />
          </div>
        )}
      </div>
      {hasMap === false && (
        <MapOptionsFields
          value={mapOptions}
          onChange={setMapOptions}
          testId="dummy-map-options"
          disabled={holdSettings}
        />
      )}
      <Collapse
        items={[
          {
            key: "customise",
            label: "Customise answers",
            children: (
              <QuestionSettings
                projectId={projectId}
                owner={owner}
                profile={profile}
                onChange={setProfile}
                disabled={holdSettings}
              />
            ),
          },
        ]}
      />
      <div>
        <DataQButton
          type="primary"
          isLoading={isGenerating}
          // Generating saves the profile: never the defaults over a saved one
          // not read yet, or one that could not be read. Whether the map
          // options go with it waits for the project's files.
          isDisabled={isLoading || state === undefined || !mapKnown}
          onClick={() => void run(false)}
        >
          Generate
        </DataQButton>
      </div>
      {result && (
        <DummyRunReport
          report={result.report}
          importWarnings={(result.importWarnings ?? "")
            .split("\n")
            .filter((w) => w.trim() !== "")}
          onRegenerateMap={onRegenerateMap}
          isRegenerating={isRegenerating}
          mapRegenerated={mapRegenerated}
          // Offered while the data is dummy data; the click checks that the
          // CSV is still the run's.
          canDownloadCsv={!!state?.active}
          onDownloadCsv={() => void downloadCsv()}
          onDownloadMap={() => void download("map")}
          onGoToTables={() =>
            navigate(`${ROUTES.PROJECT(projectId)}?tab=${TABLES_TAB}`)
          }
          onGoToDashboards={() =>
            navigate(`${ROUTES.PROJECT(projectId)}?tab=${DASHBOARDS_TAB}`)
          }
        />
      )}
      <Modal
        title="Regenerate the map"
        open={askedMap !== null}
        destroyOnHidden
        okText="Regenerate"
        confirmLoading={isRegenerating}
        onCancel={() => setAskedMap(null)}
        onOk={async () => {
          if (askedMap && (await regenerateMap(askedMap))) setAskedMap(null)
        }}
      >
        <p className="text-sm">
          The new map replaces the project&apos;s current map. Choose how it
          names the variables.
        </p>
        {askedMap && (
          <MapOptionsFields
            value={askedMap}
            onChange={setAskedMap}
            testId="dummy-regenerate-map-options"
          />
        )}
      </Modal>
    </div>
  )
}

export default DummyDataPanel
